import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Tooltip } from "@mui/material";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Box,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { connect } from "react-redux";
import { getQAs } from "../../redux/actions/candidate";
import { useParams, useNavigate } from "react-router-dom";

let BASE_URL =
  localStorage.getItem("REACT_APP_API_URL") || process.env.REACT_APP_API_URL;
let orgName = localStorage.getItem("org_name");
let API_URL = "";
if (orgName) {
  API_URL = `${BASE_URL}/${orgName}/api/v1`;
} else {
  API_URL = `${BASE_URL}/api/v1`;
}

const InterviewDetails = ({ getQAs, qaData }) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const { candidateId, threadId } = useParams();
  const [currentAns, setCurrentAns] = useState("");
  const [currentQues, setCurrentQues] = useState("");
  const [videos, setVideos] = useState([]);
  const [qaId, setQaId] = useState(null);
  const [currentVideoNumber, setCurrentVideoNumber] = useState(0);
  const [totalVideos, setTotalVideos] = useState(0);
  const [userName, setUserName] = useState("");

  // Modal state for screen recordings
  const [openMcq, setOpenMcq] = useState(false);
  const [openCoding, setOpenCoding] = useState(false);
  const [openInterview, setOpenInterview] = useState(false);
  const [mcqStatus, setMcqStatus] = useState(false);
  const [codingStatus, setCodingStatus] = useState(false);

  // Fetch QAs
  useEffect(() => {
    if (candidateId && threadId) getQAs(candidateId, threadId);
  }, [threadId, candidateId, getQAs]);

  // Populate from qaData
  useEffect(() => {
    if (qaData?.QAs?.length > 0) {
      setTotalVideos(qaData.QAs.length);
      setVideos(qaData.QAs);
      setQaId(qaData.QAs[0].intqa_id);
      setCurrentAns(qaData.QAs[0].intqa_answer);
      setCurrentQues(qaData.QAs[0].intqa_question);
      setCurrentVideoNumber(0);
    }
    if (qaData?.user?.u_name) {
      setUserName(qaData.user.u_name);
    }
    if (qaData?.applicant) {
      setMcqStatus(Boolean(qaData.applicant.apct_mcq_complete));
      setCodingStatus(Boolean(qaData.applicant.apct_coding_complete));
    }
  }, [qaData]);

  // Only reset/reload the MAIN interview video element when the selected QA changes
  useEffect(() => {
    if (videoRef.current) {
      // Pause and force the video element to pick up the updated <source> (depends on qaId)
      videoRef.current.pause();
      videoRef.current.load();
    }
  }, [qaId]);

  const handleVideoEnd = useCallback(() => {
    if (currentVideoNumber + 1 < totalVideos) {
      const next = videos[currentVideoNumber + 1];
      onClickPlay(next, currentVideoNumber + 1);
    }
  }, [currentVideoNumber, totalVideos, videos]);

  const startAgain = useCallback(() => {
    const first = videos[0];
    if (first) onClickPlay(first, 0);
  }, [videos]);

  const onClickPlay = useCallback((qa, i) => {
    setCurrentVideoNumber(i);
    setCurrentAns(qa.intqa_answer);
    setCurrentQues(qa.intqa_question);
    setQaId(qa.intqa_id);
  }, []);

  // No tabs anymore; use modal dialogs instead

  // Memoized video blocks to prevent unnecessary re-renders
  const MCQVideo = memo(function MCQVideo({
    enabled,
    src,
  }) {
    if (!enabled) {
      return (
        <Typography variant="body1" align="center">
          MCQ screen recording not available.
        </Typography>
      );
    }
    return (
      <video
        style={{ width: "70%", margin: "0 auto", display: "block" }}
        controls
      >
        <source src={src} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );
  });

  const CODINGVideo = memo(function CODINGVideo({
    enabled,
    src,
  }) {
    if (!enabled) {
      return (
        <Typography variant="body1" align="center">
          Coding screen recording not available.
        </Typography>
      );
    }
    return (
      <video
        style={{ width: "70%", margin: "0 auto", display: "block" }}
        controls
      >
        <source src={src} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );
  });

  const apiBase = useMemo(() => API_URL, []);
  const mcqSrc = useMemo(
    () => `${apiBase}/candidates/screen_recording/${candidateId}/mcq`,
    [apiBase, candidateId]
  );
  const codingSrc = useMemo(
    () => `${apiBase}/candidates/screen_recording/${candidateId}/coding`,
    [apiBase, candidateId]
  );
  const interviewSrc = useMemo(
    () => `${apiBase}/candidates/screen_recording/${candidateId}/interview`,
    [apiBase, candidateId]
  );

  return (
    <>
      <Grid
        container
        xs={12}
        sm={12}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        {/* Left: Questions list */}
        <Grid
          xs={12}
          sm={3}
          item
          sx={{
            padding: "10px",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              padding: "0 10px",
              justifyContent: "space-between",
            }}
          >
            <Typography>
              <strong>{userName}</strong>
            </Typography>
            <Typography>
              Question : {currentVideoNumber + 1}/{totalVideos}
            </Typography>
          </Stack>

          <div className="div-ques-list">
            {videos.map((c, i) => (
              <Card
                sx={{
                  display: "flex",
                  backgroundColor:
                    i === currentVideoNumber ? "lightgray" : "white",
                  cursor: "pointer",
                }}
                key={c.intqa_id}
                onClick={() => onClickPlay(c, i)}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flex: "1 0 auto" }}>
                    <div className="div-ques">
                      <strong> {i + 1} : </strong> {c.intqa_question}
                    </div>
                    <div>
                      {c.violations?.voiceValidation && (
                        <Tooltip title={c.violations.voiceValidation} arrow>
                          <span style={{ color: "red" }}>⚠ Voice Issue</span>
                        </Tooltip>
                      )}
                      {c.violations?.faceValidation && (
                        <Tooltip title={c.violations.faceValidation} arrow>
                          <span style={{ color: "red" }}>⚠ Face Issue</span>
                        </Tooltip>
                      )}
                      {c.violations?.lipSyncValidation && (
                        <Tooltip title={c.violations.lipSyncValidation} arrow>
                          <span style={{ color: "red" }}>⚠ Lip Sync Issue</span>
                        </Tooltip>
                      )}
                      {c.violations?.phoneValidation && (
                        <Tooltip title={"Cell Phone Detected"} arrow>
                          <span style={{ color: "red" }}>
                            ✔ Cell Phone Detected
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </CardContent>
                </Box>
              </Card>
            ))}
          </div>
        </Grid>

        {/* Center: Main interview Q&A video */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <video
              ref={videoRef}
              width="100%"
              height="100%"
              controls
              onEnded={handleVideoEnd}
              className="shadow-video"
            >
              <source
                src={`${API_URL}/candidates/ans_video/${candidateId}/${qaId}`}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </Grid>

        {/* Right: Current Q & A text */}
        <Grid
          xs={12}
          sm={3}
          item
          sx={{
            paddingLeft: "10px",
            marginTop: "-15px",
          }}
        >
          <div className="div-ques-list">
            {currentQues && (
              <Card>
                <CardContent>
                  <Typography
                    sx={{ mb: 1.5 }}
                    color="text.secondary"
                    align="justify"
                    flexGrow={1}
                  >
                    <strong> Question : </strong>
                    {currentQues}
                  </Typography>
                </CardContent>
              </Card>
            )}
            {currentAns && (
              <Card>
                <CardContent>
                  <Typography
                    sx={{ mb: 1.5 }}
                    color="text.secondary"
                    align="justify"
                    flexGrow={1}
                  >
                    <strong> Answer : </strong>
                    {currentAns}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </div>
        </Grid>
      </Grid>

      <Divider />

      {/* Modal triggers for screen recordings */}
      <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
        <Button variant="contained" size="small" onClick={() => setOpenMcq(true)}>
          View MCQ Recording
        </Button>
        <Button variant="contained" size="small" onClick={() => setOpenCoding(true)}>
          View Coding Recording
        </Button>
        <Button variant="contained" size="small" onClick={() => setOpenInterview(true)}>
          View Interview Recording
        </Button>
      </Stack>

      {/* MCQ Recording Modal */}
      <Dialog open={openMcq} onClose={() => setOpenMcq(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          MCQ Screen Recording
          <IconButton
            aria-label="close"
            onClick={() => setOpenMcq(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: "10px", textAlign: "center" }}>
            <MCQVideo enabled={mcqStatus} src={mcqSrc} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            component="a"
            href={mcqSrc}
            download={`candidate-${candidateId}-mcq.webm`}
            disabled={!mcqStatus}
          >
            Share Video URL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coding Recording Modal */}
      <Dialog open={openCoding} onClose={() => setOpenCoding(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          Coding Screen Recording
          <IconButton
            aria-label="close"
            onClick={() => setOpenCoding(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: "10px", textAlign: "center" }}>
            <CODINGVideo enabled={codingStatus} src={codingSrc} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            component="a"
            href={codingSrc}
            download={`candidate-${candidateId}-coding.webm`}
            disabled={!codingStatus}
          >
            Share Video URL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interview Recording Modal */}
      <Dialog open={openInterview} onClose={() => setOpenInterview(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          Interview Screen Recording
          <IconButton
            aria-label="close"
            onClick={() => setOpenInterview(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: "10px", textAlign: "center" }}>
            <video
              style={{ width: "70%", margin: "0 auto", display: "block" }}
              controls
            >
              <source src={interviewSrc} type="video/webm" />
              Your browser does not support the video tag.
            </video>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            component="a"
            href={interviewSrc}
            download={`candidate-${candidateId}-interview.webm`}
          >
            Share Video URL
          </Button>
        </DialogActions>
      </Dialog>

      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
        }}
      >
        <Button
          variant="text"
          size={"small"}
          sx={{ mt: 1, color: "primary" }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>
    </>
  );
};

const mapStateToProps = (state) => ({
  qaData: state.candidate.qaData,
  user: state.auth.user,
});

const mapDispatchToProps = {
  getQAs,
};

export default connect(mapStateToProps, mapDispatchToProps)(InterviewDetails);
